const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS

export const RU_SHORT_MONTHS = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"]
export const EN_SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

function isRu(locale: string) {
  return locale.toLowerCase().startsWith("ru")
}

function parseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

export function formatShortDate(iso: string | null | undefined, locale: string, now: Date = new Date()): string {
  const date = parseDate(iso)
  if (!date) return "—"
  const ru = isRu(locale)
  const month = (ru ? RU_SHORT_MONTHS : EN_SHORT_MONTHS)[date.getMonth()]
  const day = date.getDate()
  const withYear = date.getFullYear() !== now.getFullYear()
  if (ru) return withYear ? `${day} ${month} ${date.getFullYear()}` : `${day} ${month}`
  return withYear ? `${month} ${day}, ${date.getFullYear()}` : `${month} ${day}`
}

export function formatShortDateAfterDays(days: number, locale: string, now: Date = new Date()): string {
  return formatShortDate(new Date(now.getTime() + days * 24 * HOUR_MS).toISOString(), locale, now)
}

function ruPlural(value: number, one: string, few: string, many: string): string {
  const mod100 = value % 100
  if (mod100 >= 11 && mod100 <= 14) return many
  const mod10 = value % 10
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}

function enPlural(value: number, one: string): string {
  return value === 1 ? one : `${one}s`
}

/** Полных календарных месяцев между датами (неполный месяц не считается). */
function fullMonthsBetween(from: Date, to: Date): number {
  let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth())
  if (to.getDate() < from.getDate()) months -= 1
  return months
}

/**
 * Сколько времени прошло с даты, с округлением вниз: «5 дней», «3 месяца»,
 * «2 года 3 месяца». Для карточки человека это читается лучше календарной даты.
 */
export function formatDurationSince(iso: string | null | undefined, locale: string, now: Date = new Date()): string {
  const date = parseDate(iso)
  if (!date || date.getTime() > now.getTime()) return "—"
  const ru = isRu(locale)

  const months = fullMonthsBetween(date, now)
  if (months >= 12) {
    const years = Math.floor(months / 12)
    const restMonths = months % 12
    const yearsText = ru ? `${years} ${ruPlural(years, "год", "года", "лет")}` : `${years} ${enPlural(years, "year")}`
    if (restMonths === 0) return yearsText
    const monthsText = ru
      ? `${restMonths} ${ruPlural(restMonths, "месяц", "месяца", "месяцев")}`
      : `${restMonths} ${enPlural(restMonths, "month")}`
    return `${yearsText} ${monthsText}`
  }
  if (months >= 1) {
    return ru ? `${months} ${ruPlural(months, "месяц", "месяца", "месяцев")}` : `${months} ${enPlural(months, "month")}`
  }

  const days = Math.floor((startOfDay(now) - startOfDay(date)) / (24 * HOUR_MS))
  if (days >= 1) {
    return ru ? `${days} ${ruPlural(days, "день", "дня", "дней")}` : `${days} ${enPlural(days, "day")}`
  }
  return ru ? "меньше дня" : "less than a day"
}

/** Заголовок дня в ленте: «Сегодня», «Вчера», иначе короткая дата. */
export function formatDayGroup(iso: string | null | undefined, locale: string, now: Date = new Date()): string {
  const date = parseDate(iso)
  if (!date) return "—"
  const days = Math.round((startOfDay(now) - startOfDay(date)) / (24 * HOUR_MS))
  const ru = isRu(locale)
  if (days === 0) return ru ? "Сегодня" : "Today"
  if (days === 1) return ru ? "Вчера" : "Yesterday"
  return formatShortDate(iso, locale, now)
}

export function formatLastSeen(iso: string | null | undefined, locale: string, now: Date = new Date()): string {
  const date = parseDate(iso)
  if (!date) return "—"
  const ru = isRu(locale)
  const diff = now.getTime() - date.getTime()

  if (diff < MINUTE_MS) return ru ? "сейчас" : "just now"
  if (diff < HOUR_MS) {
    const minutes = Math.floor(diff / MINUTE_MS)
    return ru ? `${minutes} мин назад` : `${minutes} min ago`
  }

  const days = Math.round((startOfDay(now) - startOfDay(date)) / (24 * HOUR_MS))
  if (days === 0) {
    const hours = Math.floor(diff / HOUR_MS)
    return ru ? `${hours} ч назад` : `${hours} h ago`
  }
  if (days === 1) return ru ? "вчера" : "yesterday"
  return formatShortDate(iso, locale, now)
}
